import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Ship, CheckCircle2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getUser, authHeaders, API } from "@/lib/auth";

const FUELS = ["Gasolina", "Diesel", "Híbrido", "Elétrico", "GPL"];
const TRANSMISSIONS = ["Manual", "Automática", "Indiferente"];

export default function ImportPage() {
  const navigate = useNavigate();
  const user = getUser();
  const [form, setForm] = useState({
    brand: "", model: "", year_min: "", year_max: "", budget: "", km_max: "",
    fuel: "", transmission: "", listing_links: "", notes: "",
  });
  const [photos, setPhotos] = useState([]);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Precisa de iniciar sessão para pedir uma importação.");
      navigate("/entrar");
      return;
    }
    if (sending) return;
    setSending(true);
    try {
      const payload = {
        type: "importacao",
        brand: form.brand, model: form.model,
        year_min: form.year_min ? Number(form.year_min) : null,
        year_max: form.year_max ? Number(form.year_max) : null,
        budget: form.budget ? Number(form.budget) : null,
        km_max: form.km_max ? Number(form.km_max) : null,
        fuel: form.fuel, transmission: form.transmission,
        listing_links: form.listing_links, notes: form.notes,
      };
      const { data: proc } = await axios.post(`${API}/client/processes`, payload, { headers: authHeaders() });
      // Anexar fotografias ao processo (categoria documentação automóvel)
      if (photos.length > 0) {
        const data = new FormData();
        photos.forEach((f) => data.append("files", f));
        await axios.post(`${API}/client/documents?category=${encodeURIComponent("Documentação automóvel")}`, data, { headers: authHeaders() });
      }
      setDone(true);
      toast.success("Pedido de importação enviado!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar o pedido.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-40 pb-32 max-w-lg mx-auto px-5 text-center" data-testid="import-success">
          <span className="inline-flex w-16 h-16 items-center justify-center bg-emerald-50 text-emerald-600 mb-6">
            <CheckCircle2 className="w-8 h-8" />
          </span>
          <h1 className="font-display text-3xl text-[#0072C6]">Pedido Recebido</h1>
          <p className="mt-4 text-slate-600">O seu pedido de importação foi registado. Acompanhe o estado na sua área de cliente — será notificado a cada atualização.</p>
          <Link to="/conta" data-testid="import-success-account"
            className="mt-8 inline-block bg-[#0072C6] text-white font-semibold px-8 py-4 text-sm hover:bg-[#005FA3]">
            Ir para a Minha Conta
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="import-page">
      <Header />
      <main className="pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="flex items-center gap-4 mb-3">
            <span className="w-12 h-12 flex items-center justify-center bg-[#0072C6] text-white">
              <Ship className="w-6 h-6" />
            </span>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight text-[#0072C6]">Importe o Seu Automóvel</h1>
          </div>
          <p className="text-slate-600 max-w-2xl mb-10">
            Preencha o formulário com a viatura que procura. Tratamos da pesquisa, cotação, transporte e legalização.
            Acompanhe todo o processo na sua área de cliente.
          </p>

          {!user && (
            <div className="mb-8 bg-[#F8FAFC] border border-slate-200 p-5 flex flex-wrap items-center justify-between gap-4" data-testid="import-login-notice">
              <p className="text-sm text-slate-600">Para acompanhar o seu pedido, precisa de uma conta de cliente.</p>
              <Link to="/entrar" className="text-sm font-semibold text-[#0072C6] underline">Entrar ou criar conta</Link>
            </div>
          )}

          <form onSubmit={submit} className="bg-[#F8FAFC] border border-slate-200 p-7 sm:p-10" data-testid="import-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Marca pretendida *</label>
                <input data-testid="imp-brand" required value={form.brand} onChange={set("brand")} placeholder="Ex.: BMW"
                  className="w-full border border-slate-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#0072C6]" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Modelo *</label>
                <input data-testid="imp-model" required value={form.model} onChange={set("model")} placeholder="Ex.: 320d Touring"
                  className="w-full border border-slate-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#0072C6]" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Ano mínimo</label>
                <input data-testid="imp-year-min" type="number" min={1990} value={form.year_min} onChange={set("year_min")} placeholder="2019"
                  className="w-full border border-slate-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#0072C6]" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Ano máximo</label>
                <input data-testid="imp-year-max" type="number" min={1990} value={form.year_max} onChange={set("year_max")} placeholder="2023"
                  className="w-full border border-slate-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#0072C6]" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Orçamento (€)</label>
                <input data-testid="imp-budget" type="number" min={0} value={form.budget} onChange={set("budget")} placeholder="30000"
                  className="w-full border border-slate-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#0072C6]" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Quilometragem máxima</label>
                <input data-testid="imp-km" type="number" min={0} value={form.km_max} onChange={set("km_max")} placeholder="80000"
                  className="w-full border border-slate-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#0072C6]" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Combustível</label>
                <select data-testid="imp-fuel" value={form.fuel} onChange={set("fuel")}
                  className="w-full border border-slate-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#0072C6]">
                  <option value="">Indiferente</option>
                  {FUELS.map((f) => <option key={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Caixa de velocidades</label>
                <select data-testid="imp-transmission" value={form.transmission} onChange={set("transmission")}
                  className="w-full border border-slate-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#0072C6]">
                  <option value="">Indiferente</option>
                  {TRANSMISSIONS.filter((t) => t !== "Indiferente").map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Links de anúncios (opcional — um por linha)</label>
                <textarea data-testid="imp-links" rows={3} value={form.listing_links} onChange={set("listing_links")}
                  placeholder={"https://www.standvirtual.com/…\nhttps://www.mobile.de/…"}
                  className="w-full border border-slate-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#0072C6] resize-y font-mono" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Observações</label>
                <textarea data-testid="imp-notes" rows={3} value={form.notes} onChange={set("notes")}
                  placeholder="Cor, equipamento, prazo, país de preferência…"
                  className="w-full border border-slate-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#0072C6] resize-y" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Fotografias de referência (opcional)</label>
                <label data-testid="imp-photos" className="inline-flex items-center gap-2 border border-slate-200 bg-white text-slate-700 text-sm font-medium px-5 py-3 cursor-pointer hover:border-[#0072C6]">
                  Escolher ficheiros
                  <input type="file" multiple accept="image/*" className="hidden" onChange={(e) => setPhotos(Array.from(e.target.files || []))} />
                </label>
                {photos.length > 0 && <p className="mt-2 text-xs text-slate-500">{photos.length} ficheiro(s) selecionado(s)</p>}
              </div>
            </div>

            <button type="submit" data-testid="imp-submit" disabled={sending}
              className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-[#0072C6] text-white font-semibold px-8 py-4 text-sm tracking-wide hover:bg-[#005FA3] transition-colors disabled:opacity-60">
              {sending && <Loader2 className="w-4 h-4 animate-spin" />}
              {sending ? "A enviar…" : "Solicitar Importação"}
            </button>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}
